import { getFirestore, collection, getDocs, query, where } from "firebase/firestore";
import { app } from "../../../firebase-config.js";
import { locale } from "../../../services/LocaleService.js";
import { JsonLdInjector } from "../seo/JsonLdInjector.js";

const db = getFirestore(app);

/**
 * UniversalViewer (R.A.P.S Engine)
 * Non-Destructive Image Context Overlay System
 */
export const UniversalViewer = {
    // State
    isActive: false,
    currentAbortController: null,
    observer: null,

    /**
     * Show Overlay (Activate Translation)
     * @param {Object} params
     * @param {string} params.seriesId
     * @param {string} params.episodeId
     * @param {string} params.lang - Target Language (e.g., 'ja', 'en')
     * @param {string} params.mode - 'webtoon' | 'davinci'
     * @param {Array} params.images - Optional: For Webtoon index mapping if needed
     */
    show: async ({ seriesId, episodeId, lang, mode }) => {
        if (UniversalViewer.isActive) UniversalViewer.hide(); // Reset if already active
        UniversalViewer.isActive = true;
        UniversalViewer._injectStyles(); // [FIX] ID: 33 Ensure CSS exists
        UniversalViewer.currentAbortController = new AbortController();

        console.log(`[UniversalViewer] Activating... Mode: ${mode}, Lang: ${lang}, ID: ${episodeId}`);
        window.Modal.toast("AI Context Engine 가동 중...");

        try {
            // 1. Fetch OCR Data (H-TCS: Direct Access)
            // [Strategy] No Fallback. Direct Path Only.
            // Webtoon: episodeId MUST be Index ('0')
            // DaVinci: episodeId MUST be UUID
            const anchorsPath = `series/${seriesId}/episodes/${episodeId}/ocr_anchors`;

            // [DEBUG] Verify Path against User's Screenshot
            console.log(`[UniversalViewer] Querying Path: ${anchorsPath}`);
            const q = query(collection(db, anchorsPath));
            const snapshot = await getDocs(q);

            if (snapshot.empty) {
                console.warn(`[UniversalViewer] No OCR data found at: ${anchorsPath}`);
                window.Modal.toast("서버에 분석 데이터가 없습니다 (0건).<br>패스: " + anchorsPath.split('/').pop()); // Diagnostic Toast
                UniversalViewer.hide();
                return false; // [SIGNAL] No Data
            }

            window.Modal.toast(`분석 데이터 수신 성공: ${snapshot.size}개`); // Diagnostic Toast

            const ocrDataMap = {};
            const contextAggregator = { locations: new Set(), vibes: new Set() };

            snapshot.forEach(doc => {
                const data = doc.data();
                // Key: refId (Index string or Block ID)
                ocrDataMap[doc.id] = data;

                // [SEO] Aggregate Context
                if (data.context) {
                    if (data.context.location) contextAggregator.locations.add(data.context.location);
                    if (data.context.vibe) contextAggregator.vibes.add(data.context.vibe);
                }
            });

            // 2. Inject Overlay Layers
            const stats = UniversalViewer.injectOverlay(mode, ocrDataMap, lang);

            // 3. Start Cinematic Scroll Observer
            UniversalViewer.initObserver();

            // 4. [SEO] G-MITS Injection
            JsonLdInjector.inject({
                title: document.title,
                description: `Translated to ${lang}`,
                author: "Mirinae Creator",
                datePublished: new Date().toISOString(),
                ocrContext: {
                    locations: Array.from(contextAggregator.locations),
                    vibes: Array.from(contextAggregator.vibes)
                }
            });

            window.Modal.toast("번역 레이어가 적용되었습니다.");
            return { success: true, ...stats }; // [SIGNAL] Return Stats

        } catch (e) {
            console.error("[UniversalViewer] Activation Failed:", e);
            window.Modal.alert("오버레이 엔진 로드 실패");
            UniversalViewer.hide();
            return false; // [SIGNAL] Error
        }
    },

    /**
     * [FIX] Dynamic CSS Injection to ensure Animation works (Bypassing Tailwind Purge/Conflict)
     */
    _injectStyles: () => {
        if (document.getElementById('raps-dynamic-styles')) return;
        const style = document.createElement('style');
        style.id = 'raps-dynamic-styles';
        style.textContent = `
            @keyframes raps-pulse-glow {
                0% {
                    box-shadow: inset 0 0 10px 2px rgba(99, 102, 241, 0.2);
                    border-color: rgba(99, 102, 241, 0.3);
                }
                50% {
                    box-shadow: inset 0 0 30px 8px rgba(99, 102, 241, 0.6);
                    border-color: rgba(99, 102, 241, 0.8);
                }
                100% {
                    box-shadow: inset 0 0 10px 2px rgba(99, 102, 241, 0.2);
                    border-color: rgba(99, 102, 241, 0.3);
                }
            }
            .raps-active-pulse {
                border: 2px solid rgba(99, 102, 241, 0.3) !important;
                animation: raps-pulse-glow 3s infinite ease-in-out !important;
                z-index: 60;
                pointer-events: none;
            }
        `;
        document.head.appendChild(style);
    },

    /**
     * Hides the overlay and cleans up resources
     */
    hide: () => {
        UniversalViewer.isActive = false;
        if (UniversalViewer.currentAbortController) {
            UniversalViewer.currentAbortController.abort();
            UniversalViewer.currentAbortController = null;
        }

        if (UniversalViewer.observer) {
            UniversalViewer.observer.disconnect();
            UniversalViewer.observer = null;
        }

        // Remove all injected layers
        document.querySelectorAll('.raps-overlay-layer').forEach(el => el.remove());

        // [SEO] Cleanup
        JsonLdInjector.clear();

        console.log("[UniversalViewer] Deactivated.");
    },

    /**
     * Core Injection Logic
     */
    injectOverlay: (mode, dataMap, lang) => {
        if (mode === 'webtoon' || mode === 'comic') {
            // Target: .webtoon-image-wrapper
            const wrappers = document.querySelectorAll('.webtoon-image-wrapper');
            wrappers.forEach((wrapper, index) => {
                // [Direct Access] Webtoon uses Index (0, 1, 2...)
                // We trust the dataMap keys match the image index

                // 1. Try UUID/Regex Check (Legacy support, just in case)
                const img = wrapper.querySelector('img');
                let data = null;

                if (img && img.src) {
                    const src = decodeURIComponent(img.src);
                    const match = src.match(/img_([a-z0-9]+)_/);
                    if (match && dataMap[match[1]]) {
                        data = dataMap[match[1]];
                    }
                }

                // 2. Try Exact Filename Match (Modern UUID & Legacy)
                // [FIX] URL -> Filename -> Anchor ID
                if (!data && img && img.src) {
                    try {
                        const src = decodeURIComponent(img.src);
                        // Remove query params (?token=...)
                        const cleanUrl = src.split('?')[0];
                        // Extract basename (e.g., '1738..._uuid.webp' or 'image_0.png')
                        const filename = cleanUrl.substring(cleanUrl.lastIndexOf('/') + 1);

                        // A. Exact Filename
                        if (dataMap[filename]) {
                            data = dataMap[filename];
                        }

                        // B. Timestamp Extraction (ocr.trigger.js logic)
                        if (!data) {
                            const tsMatch = filename.match(/(\d{10,})/);
                            if (tsMatch && dataMap[tsMatch[1]]) {
                                data = dataMap[tsMatch[1]];
                                console.log(`[UniversalViewer] Match (Timestamp): ${tsMatch[1]}`);
                            }
                        }

                        // C. No Extension
                        if (!data) {
                            const noExt = filename.replace(/\.(webp|png|jpg|jpeg)$/i, '');
                            if (dataMap[noExt]) {
                                data = dataMap[noExt];
                            }
                        }
                    } catch (e) {
                        console.warn("[UniversalViewer] Filename extraction failed:", e);
                    }
                }



                // 3. Try Index Check (Standard)
                if (!data) data = dataMap[String(index)];

                if (data && data.anchors) {
                    const layer = UniversalViewer.createLayer(data, lang);
                    layer.dataset.index = index;
                    wrapper.style.position = 'relative'; // Ensure positioning context

                    // [FIX] Hide Overlay until Image Loads (Prevent "Lines" glitch on 0-width wrapper)
                    if (img && !img.complete) {
                        layer.style.opacity = '0';
                        layer.style.transition = 'opacity 0.3s ease';
                        img.addEventListener('load', () => {
                            layer.style.opacity = '1';
                        });
                    } else if (img && img.complete) {
                        // If already loaded, ensure visible (transition might be needed if initially hidden by CSS class?)
                        layer.style.opacity = '1';
                    }

                    wrapper.appendChild(layer);

                    // [FIX] Force Wrapper to Match Image Dimensions (Comic Mode / Wide Screen Fix)
                    // CSS 'fit-content' fails when aspect ratio forces height constraint.
                    if (mode === 'comic' && img) {
                        const syncWrapperSize = () => {
                            // Use offsetWidth/Height or getBoundingClientRect
                            // We want the wrapper to be exactly the image size
                            if (img.width > 0 && img.height > 0) {
                                wrapper.style.width = `${img.width}px`;
                                wrapper.style.height = `${img.height}px`;
                            }
                        };

                        // Initial Sync
                        if (img.complete) syncWrapperSize();
                        img.addEventListener('load', syncWrapperSize);

                        // Responsive Sync
                        const resizeObserver = new ResizeObserver(() => {
                            syncWrapperSize();
                        });
                        resizeObserver.observe(img);
                    }
                }
            });
        } else if (mode === 'davinci') {
            // Target: .dv-block[data-type="image"] .dv-image-wrapper

            const blocks = document.querySelectorAll('.dv-block[data-type="image"]');
            let matchCount = 0;
            const availableKeys = Object.keys(dataMap);
            console.log(`[UniversalViewer] Available OCR Keys:`, availableKeys);

            blocks.forEach(block => {
                const wrapper = block.querySelector('.dv-image-wrapper');
                const img = wrapper ? wrapper.querySelector('img') : null;

                if (wrapper && img && img.src) {
                    try {
                        // [BLOCK ID STRATEGY]
                        // Primary: Use the stable Block ID from DOM
                        const blockId = block.dataset.id;
                        let matchedData = null;
                        let matchType = 'none';

                        if (blockId && dataMap[blockId]) {
                            matchedData = dataMap[blockId];
                            matchType = 'block_id';
                        }

                        // Fallback: Legacy URL/Timestamp Logic
                        if (!matchedData) {
                            const url = img.src;
                            const decoded = decodeURIComponent(url);
                            const path = decoded.split('?')[0];
                            const fileNameWithExt = path.substring(path.lastIndexOf('/') + 1);
                            const refId = fileNameWithExt.replace(/\.(webp|png|jpg|jpeg)$/i, '');

                            if (dataMap[refId]) {
                                matchedData = dataMap[refId];
                                matchType = 'legacy_timestamp';
                            } else {
                                // [DIAGNOSTIC] Log why fallback failed
                                console.warn(`[UniversalViewer] Lookup Failed. BlockID: ${blockId} (In Map: ${!!dataMap[blockId]}), RefID: ${refId} (In Map: ${!!dataMap[refId]})`);

                                // ID Mismatch Visualization
                                const failLayer = document.createElement('div');
                                failLayer.className = 'absolute inset-0 pointer-events-none z-[60] flex items-center justify-center';
                                failLayer.style.border = '4px solid blue';
                                failLayer.style.backgroundColor = 'rgba(0, 0, 255, 0.1)';

                                const label = document.createElement('div');
                                label.className = 'bg-blue-900 text-white px-4 py-2 rounded shadow-lg text-lg font-bold pointer-events-auto';
                                label.innerHTML = `ID MISMATCH<br><span class="text-sm font-mono">${blockId || refId}</span>`;

                                failLayer.appendChild(label);
                                wrapper.style.position = 'relative';
                                wrapper.appendChild(failLayer);
                            }
                        }

                        if (matchedData) {
                            // [SUCCESS]
                            const layer = UniversalViewer.createLayer(matchedData, lang);
                            layer.dataset.refId = blockId; // Bind to Block ID if possible or RefID? Actually irrelevant for layer logic, but good for debug
                            wrapper.style.position = 'relative';
                            wrapper.appendChild(layer);
                            matchCount++;
                            console.log(`[UniversalViewer] Match (${matchType}): ${blockId}`);
                        }

                    } catch (e) {
                        console.warn("[UniversalViewer] DaVinci ID Extraction Failed:", e);
                    }
                }
            });

            if (matchCount === 0 && availableKeys.length > 0) {
                console.warn(`[UniversalViewer] ⚠️ CRITICAL: 0 Matches found but ${availableKeys.length} keys exist.`);
                // window.Modal.alert(`매칭 실패!<br>...`); // Suppressed for visual check
            } else {
                console.log(`[UniversalViewer] DaVinci Overlay Success: ${matchCount} layers matched.`);
                window.Modal.toast(`이미지 ${matchCount}장에 번역 적용됨`);
            }

            // [FIX] Return Stats for Auto-Heal
            return { matched: matchCount, total: blocks.length };
        }
    },

    /**
     * Create the Overlay DOM Element
     */
    createLayer: (ocrData, lang) => {
        const layer = document.createElement('div');
        // [VISUAL UPDATE] Use 'raps-active-pulse' (Option A: Breath of AI) instead of red debug border
        // [FIX] Z-Index: Must be higher than Protector(30) but lower than BottomTab(40) to prevent blocking
        layer.className = 'raps-overlay-layer raps-active-pulse absolute inset-0 pointer-events-none z-[35] transition-opacity duration-500';
        layer.style.opacity = '1';
        // Removed: layer.style.border = '4px solid red'; 
        // Removed: layer.style.backgroundColor = 'rgba(255, 0, 0, 0.1)';

        // Dynamic Backdrop (Vibe)
        if (ocrData.context && ocrData.context.vibe) {
            layer.dataset.vibe = ocrData.context.vibe;
            // CSS logic will handle styling based on data-vibe
        }

        // Render Anchors (Async wrapper not natively supported in createLayer, but we can do it inside)
        // [Refactor] Since createLayer is called synchronously, we can't await inside efficiently without refactoring injectOverlay.
        // Instead, we will construct elements with "Loading..." or original text, then update them.

        ocrData.anchors.forEach(async (anchor) => {
            const bubble = document.createElement('div');
            // Basic RAPS Positioning
            bubble.className = 'raps-anchor absolute flex items-center justify-center text-center p-2 transition-transform duration-300';

            // Percentage Coordinates
            // Default to box_percent, fallback to box_2d if needed
            let { top, left, width, height } = anchor.box_percent || anchor.box_2d || { top: 0, left: 0, width: 0, height: 0 };

            // [FIX] Normalization: Handle 0-100 range (Gemini sometimes returns 50 instead of 0.5)
            // If value > 1, assume it's already a percentage (0-100).
            const normalize = (val) => (val > 1 ? val : val * 100);

            bubble.style.top = `${normalize(top)}%`;
            bubble.style.left = `${normalize(left)}%`;
            bubble.style.width = `${normalize(width)}%`;
            bubble.style.height = `${normalize(height)}%`;

            // Inner Content
            const contentDiv = document.createElement('div');
            contentDiv.className = "raps-content backdrop-blur-md bg-black/60 text-white rounded-lg px-3 py-2 text-sm md:text-base font-medium shadow-lg hover:scale-105 transition-transform pointer-events-auto cursor-pointer flex items-center justify-center min-w-[20px] min-h-[20px]";

            // Initial State: Original Text or Spinner
            contentDiv.innerText = anchor.text;

            bubble.appendChild(contentDiv);
            layer.appendChild(bubble);

            // [Translation]
            try {
                // Dynamically import to avoid circular dep if any (Standard Import is fine though)
                const { templateTranslationService } = await import("../../../services/TranslationService.js");
                const translated = await templateTranslationService.translate(anchor.text, lang);
                contentDiv.innerText = translated;
                contentDiv.classList.add('bg-indigo-600/80'); // Active Highlight
            } catch (e) {
                console.warn("[UniversalViewer] Translation Error:", e);
                // Keep original text on fail
            }
        });

        // [DIAGNOSTIC] Confirm Bubble Creation
        if (ocrData.anchors.length > 0) {
            console.log(`[Diagnostic] Layer created with ${ocrData.anchors.length} bubbles.`);
        }

        return layer;
    },

    /**
     * Cinematic Scroll Observer
     * Reveals text only when the image is in the viewport (Focus)
     */
    initObserver: () => {
        const options = {
            root: null, // Viewport
            rootMargin: '-10% 0px -10% 0px', // Active zone in middle 80%
            threshold: 0.3 // Trigger when 30% visible
        };

        /* [DIAGNOSTIC v1.36.279] Observer Disabled to rule out visibility logic
        UniversalViewer.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.opacity = '1';
                    entry.target.classList.add('raps-active');
                } else {
                    entry.target.style.opacity = '0';
                    entry.target.classList.remove('raps-active');
                }
            });
        }, options);

        document.querySelectorAll('.raps-overlay-layer').forEach(el => {
            UniversalViewer.observer.observe(el);
        });
        */
        console.log("[UniversalViewer] Observer Disabled for Diagnostic v1.36.279");
    }
};
